import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { ButtonComponent as ButtonComponent_ } from "./interfaces/base";
import styled from "@emotion/styled";
import { Link, useLocation } from "react-router-dom";
import RenderChildren from "./RenderChildren";
import BackgroundVideoComponent from "./BackgroundVideoComponent";
import useLazyLoad from "./utilities/useLazyLoad";
import { getPageLocaleUrl } from "./utilities/helpers";
import {
  appendForwardedUrlParams,
  resolveBuilderLinkHref,
} from "./utilities/linkHref";
import MegaMenuComponent from "./MegaMenuComponent";
import { EditorContent } from "@tiptap/react";
import {
  getBuilderDomId,
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import { useShoppingCart } from "./context/ShoppingCartContext";
import { getProductPageBaseHref } from "./utilities/catalogProductAdapter";
import {
  buildCatalogProductDetailHref,
  hasVariants,
} from "./utilities/productCatalog";
import usePageLanguage from "./hooks/usePageLanguage";

const Button = styled("button")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

export default function ButtonComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: ButtonComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const typographies = useAppSelector((state) => state.builder.typographies);
  const colors = useAppSelector((state) => state.builder.colors);
  const [style, setStyle] = useState<React.CSSProperties>();
  const [hoverStyle, setHoverStyle] = useState<any>();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const isBuilder = document.getElementById("BuilderContent");
  let editor = useAppSelector((state) => state.builder?.editor?.editor);
  const [backgroundStyles, setBackgroundStyles] =
    useState<React.CSSProperties>();

  const [borderColors, setBorderColors] = useState<any>();

  const builderEditor = useAppSelector((state) => state.builder.editor);

  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  const component = useAppSelector(
    (state) => state.builder.structure.components
  ) as any;

  const [backgroundImage, setBackgroundImage] = useState(
    attributes.styles.backgroundImage ? attributes.styles.backgroundImage : ""
  );

  const buttonRefElement = useRef<HTMLButtonElement>(null);
  const loaded = useLazyLoad(buttonRefElement, backgroundImage);

  const pages = useAppSelector((state) => state.builder.pages);
  const location = useLocation();
  const [templateEditText, setTemplateEditText] = useState(false);
  const lang = usePageLanguage();
  const { increaseCartQuantity, getCatalogProductByUuid } = useShoppingCart();

  const isAddToCartMode = attributes.type === "add_to_cart";
  const isSubmitMode =
    attributes.type === "submit" || attributes.buttonType === "submit";
  const htmlButtonType = isSubmitMode ? "submit" : "button";

  const linkWrapperAs = useMemo(() => {
    if (isAddToCartMode || isSubmitMode) {
      return "button" as const;
    }
    if (
      attributes.link?.type === "Web" ||
      attributes.link?.type === "Web_out" ||
      attributes.link?.type === "Anchor" ||
      attributes.link?.type === "Phone" ||
      attributes.link?.type === "Email"
    ) {
      return "a" as const;
    }
    if (attributes.link?.type === "Page") {
      return attributes.keepUrlParams && location.search ? ("a" as const) : Link;
    }
    return null;
  }, [
    isAddToCartMode,
    isSubmitMode,
    attributes.link?.type,
    attributes.keepUrlParams,
    location.search,
  ]);

  // Builder text editor toolbar uses <button> elements; avoid nesting inside <button>.
  const wrapperAs =
    isBuilder && (linkWrapperAs === "button" || linkWrapperAs === null)
      ? "div"
      : linkWrapperAs ?? (isBuilder ? "div" : "button");

  const usesCompactButtonLayout =
    isAddToCartMode ||
    isSubmitMode ||
    attributes.link?.type === "Web" ||
    attributes.link?.type === "Page" ||
    attributes.link?.type === "Web_out" ||
    attributes.link?.type === "Anchor" ||
    attributes.link?.type === "Phone" ||
    attributes.link?.type === "Email" ||
    attributes.link?.type === "None" ||
    !attributes.link?.type;

  const buttonLayoutStyle = useMemo((): React.CSSProperties | null => {
    if (!usesCompactButtonLayout) {
      return null;
    }

    const savedWidth = attributes.styles?.width;
    const layout: React.CSSProperties = {
      textDecoration: "none",
      display: attributes.styles?.display
        ? attributes.styles.display
        : "inline-grid",
      alignItems: "center",
      justifyContent: attributes.textAlign ?? "center",
    };

    // Block-level builder wrapper (div) stretches to column width unless shrink-wrapped.
    const needsShrinkWrap =
      (isBuilder && wrapperAs === "div") ||
      isAddToCartMode ||
      isSubmitMode;
    if (needsShrinkWrap && (!savedWidth || savedWidth === "auto")) {
      layout.width = "fit-content";
      layout.maxWidth = "100%";
    } else if (savedWidth) {
      layout.width = savedWidth;
    }

    return layout;
  }, [
    usesCompactButtonLayout,
    isAddToCartMode,
    isSubmitMode,
    isBuilder,
    wrapperAs,
    attributes.styles?.display,
    attributes.styles?.width,
    attributes.textAlign,
  ]);

  const addToCartProduct = useMemo(() => {
    if (!isAddToCartMode || !attributes.productUuid) {
      return undefined;
    }
    return getCatalogProductByUuid(attributes.productUuid);
  }, [
    isAddToCartMode,
    attributes.productUuid,
    getCatalogProductByUuid,
  ]);

  const addToCartProductHref = useMemo(() => {
    if (!addToCartProduct) {
      return "";
    }
    const productPage = pages.list.find(
      (page) => page.pageType === "product" && page.languageCode === lang
    );
    const languageProductHref = getProductPageBaseHref(
      productPage,
      languages
    );
    return buildCatalogProductDetailHref(
      languageProductHref,
      addToCartProduct,
      lang
    );
  }, [addToCartProduct, pages.list, languages, lang]);

  const handleAddToCartClick = useCallback(
    (e: React.MouseEvent) => {
      if (!attributes.productUuid) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const product =
        addToCartProduct ?? getCatalogProductByUuid(attributes.productUuid);
      if (!product) {
        return;
      }
      if (hasVariants(product)) {
        if (addToCartProductHref) {
          window.location.href = addToCartProductHref;
        }
        return;
      }
      increaseCartQuantity(
        product.productUuid,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );
    },
    [
      attributes.productUuid,
      addToCartProduct,
      addToCartProductHref,
      getCatalogProductByUuid,
      increaseCartQuantity,
    ]
  );

  useEffect(() => {
    if (attributes.styles.backgroundImage) {
      setBackgroundImage(attributes.styles.backgroundImage);
    }
  }, [attributes.styles.backgroundImage]);

  const handleMegaMenuOpen = (boolean) => {
    if (attributes.link?.type === "MegaMenu") {
      setMegaMenuOpen(boolean);
    }
  };

  const buttonComponent =
    (getComponentFromStructureMap(component, uuid ?? id) as ButtonComponent_) ??
    ({
      id: typeof id === "number" ? id : -1,
      parent_id: null,
      childElements: childElements ?? [],
      childWebpageComponentRelations: childWebpageComponentRelations ?? [],
      sort_order: 0,
      type: "Button",
      uuid: null,
      version: 1,
      webpage_id: 0,
      attributes,
    } as ButtonComponent_);
  const isEditing = matchesEditorTarget(buttonComponent, editorId);
  const domId =
    getBuilderDomId({ id, uuid }) ||
    getBuilderDomId({
      id: buttonComponent?.id,
      uuid: buttonComponent?.uuid,
    });

  useEffect(() => {
    const compTypography = typographies?.find(
      (x: any) => x.id === attributes.typography_id
    );
    if (compTypography) {
      setStyle({
        backgroundColor: attributes.styles.backgroundColor ?? "#fff",
        width: attributes.styles.width ?? "auto",

        fontSize: compTypography.fontSize + compTypography.fontSizeUnit,
        fontFamily: compTypography.fontFamily,
        fontStyle: compTypography.fontStyle,
        fontWeight: compTypography.fontWeight,
        letterSpacing: compTypography.letterSpacing,
        lineHeight: attributes.styles.lineHeight ?? compTypography.lineHeight,
        color: colors?.find((x: any) => x.id === compTypography.colorId)
          ?.color,
        transition:
          compTypography.transitionDuration +
          " color " +
          compTypography.transitionEffect,
      });
      setHoverStyle({
        "&:hover": {
          color:
            colors?.find(
              (x: any) => x.id === compTypography.linkHoverColorId
            )?.color + " !important",
        },
      });
    }
  }, [typographies, attributes]);

  // If builder, do not render submit button, or else cant edit the button while in a form

  let href = isAddToCartMode
    ? ""
    : resolveBuilderLinkHref(attributes.link, pages.list, (page) =>
        getPageLocaleUrl(page, languages)
      );

  if (attributes.keepUrlParams) {
    href = appendForwardedUrlParams(
      href,
      attributes.link?.type,
      location.search,
      location.pathname
    );
  }

  function generateLink(link: any) {
    const href = resolveBuilderLinkHref(link, pages.list, (page) =>
      getPageLocaleUrl(page, languages)
    );

    return link.type === "Page" ? (
      <Link
        className="button-link"
        key={link.text + link.type + href}
        to={href || `${location.pathname}${location.search}`}
        target={link.type !== "None" ? link.target : "_self"}
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
      ></Link>
    ) : (
      <a
        className="button-link"
        key={link.text + link.type + href}
        href={href}
        target={
          link.type !== "None" && link.type !== "" ? link.target : "_self"
        }
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
        onClick={() => {}}
        style={{
          zIndex: 8,
        }}
      ></a>
    );
  }

  useEffect(() => {
    const templateContainer = document.getElementById(
      "template_edit_over_lay_content"
    );
    const textElement = buttonRefElement.current;

    if (
      textElement &&
      templateContainer &&
      templateContainer.contains(textElement)
    ) {
      setTemplateEditText(true);
    }
  }, [editorId]);

  useEffect(() => {
    const backgroundColor = buttonComponent.attributes?.color_id
      ? colors?.find((x: any) => x.id === buttonComponent.attributes?.color_id)
          ?.color
      : buttonComponent.attributes?.styles.backgroundColor
      ? buttonComponent.attributes?.styles.backgroundColor
      : "";

    const backgroundColorHover = buttonComponent.attributes?.hover_color_id
      ? colors?.find(
          (x: any) => x.id === buttonComponent.attributes?.hover_color_id
        )?.color
      : buttonComponent.attributes?.styles[":hover"] &&
        buttonComponent.attributes?.styles[":hover"].backgroundColor
      ? buttonComponent.attributes?.styles[":hover"].backgroundColor
      : "";

    const backgroundObject = {
      backgroundColor: `${backgroundColor ? backgroundColor : "#fff"} ${
        backgroundColor ? "!important" : ""
      }`,
      ":hover": {
        backgroundColor: `${backgroundColorHover} ${
          backgroundColor ? "!important" : ""
        }`,
      },
    };

    setBackgroundStyles(backgroundObject);
  }, [
    buttonComponent.attributes,
    buttonComponent.attributes?.color_id,
    buttonComponent.attributes?.hover_color_id,
    buttonComponent.attributes?.styles.backgroundColor,
    buttonComponent.attributes?.styles[":hover"]?.backgroundColor,
    colors,
  ]);

  useEffect(() => {
    // Change border color values

    const borderColorValues = {
      borderTopColor: buttonComponent.attributes?.borderTopColorId
        ? colors?.find(
            (x: any) => x.id === buttonComponent.attributes?.borderTopColorId
          )?.color
        : buttonComponent.attributes?.styles.borderTopColor
        ? buttonComponent.attributes?.styles.borderTopColor
        : "",
      borderBottomColor: buttonComponent.attributes?.borderBottomColorId
        ? colors?.find(
            (x: any) => x.id === buttonComponent.attributes?.borderBottomColorId
          )?.color
        : buttonComponent.attributes?.styles.borderBottomColor
        ? buttonComponent.attributes?.styles.borderBottomColor
        : "",
      borderLeftColor: buttonComponent.attributes?.borderLeftColorId
        ? colors?.find(
            (x: any) => x.id === buttonComponent.attributes?.borderLeftColorId
          )?.color
        : buttonComponent.attributes?.styles.borderLeftColor
        ? buttonComponent.attributes?.styles.borderLeftColor
        : "",
      borderRightColor: buttonComponent.attributes?.borderRightColorId
        ? colors?.find(
            (x: any) => x.id === buttonComponent.attributes?.borderRightColorId
          )?.color
        : buttonComponent.attributes?.styles.borderRightColor,
    };

    setBorderColors(borderColorValues);

  
  }, [
    buttonComponent.attributes,
    buttonComponent.attributes.styles,
    buttonComponent.attributes?.borderBottomColorId,
    buttonComponent.attributes?.borderLeftColorId,
    buttonComponent.attributes?.borderRightColorId,
    buttonComponent.attributes?.borderTopColorId,
    colors,
  ]);

  return (
    <Button
      ref={buttonRefElement}
      as={wrapperAs}
      to={href || `${location.pathname}${location.search}`}
      style={buttonLayoutStyle}
      target={
        attributes.link?.type === "Web" ||
        attributes.link?.type === "None" ||
        attributes.link?.type === "Anchor" ||
        attributes.link?.type === "Phone" ||
        attributes.link?.type === "Email"
          ? "_self"
          : attributes.link?.target
      }
      href={href}
      // href={href}
      id={domId}
      type={wrapperAs === "button" ? htmlButtonType : undefined}
      data-button-html-type={htmlButtonType}
      className={
        "builder-component relative builder-component__button " +
        (attributes.classes ?? "") +
        (isEditing ? " builder-component--active" : "")
      }
      data-style={{
        ...style,
        ...attributes.styles,
        ...hoverStyle,

        backgroundImage: !isBuilder
          ? loaded
            ? loaded.map((src) => `url("${src}")`).join(", ")
            : "none"
          : attributes.styles.backgroundImage,

        // Set the border widths for each border side, left right, top and bottom

        borderLeftWidth: attributes.styles.borderLeftWidth
          ? attributes.styles.borderLeftWidth
          : "0px",
        borderRightWidth: attributes.styles.borderRightWidth
          ? attributes.styles.borderRightWidth
          : "0px",
        borderTopWidth: attributes.styles.borderTopWidth
          ? attributes.styles.borderTopWidth
          : "0px",
        borderBottomWidth: attributes.styles.borderBottomWidth
          ? attributes.styles.borderBottomWidth
          : "0px",
        ...backgroundStyles,
        backgroundColor: backgroundStyles?.backgroundColor
          ? backgroundStyles?.backgroundColor
          : "none",

        borderLeftColor: borderColors?.borderLeftColor,
        borderRightColor: borderColors?.borderRightColor,
        borderTopColor: borderColors?.borderTopColor,
        borderBottomColor: borderColors?.borderBottomColor,
      }}
      data-component-type="button"
      onClick={(e) => {
        e.stopPropagation();

        if (isBuilder) {
          e.preventDefault();
        } else if (isAddToCartMode) {
          handleAddToCartClick(e);
          return;
        }

        handleMegaMenuOpen(!megaMenuOpen);
      }}
      onMouseEnter={(e) => {
        e.preventDefault();
        handleMegaMenuOpen(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();

        handleMegaMenuOpen(false);
      }}
      draggable={false}
    >
      {attributes &&
        attributes.link &&
        attributes.link.type === "MegaMenu" &&
        attributes.link.template_id !== null && (
          <>
            <MegaMenuComponent
              id={attributes.link.template_id}
              parentId={id}
              menuOpen={megaMenuOpen}
              setMegaMenuOpen={setMegaMenuOpen}
              leftPosition={attributes.link?.left_position}
              topPosition={attributes.link?.top_position}
              activateType={attributes.link?.activate_type}
            />
          </>
        )}

      {attributes &&
        attributes.link &&
        attributes.link.type === "Dropdown" &&
        attributes.links &&
        attributes.links.length > 0 && (
          <>
            <div className="button-subnav">
              {attributes.links && attributes.links.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {attributes.links.map((item, i) => generateLink(item))}
                </div>
              )}
            </div>
          </>
        )}

      <BackgroundVideoComponent componentKey={uuid} />
      <RenderChildren
        key={domId + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      {!isBuilder && (
        // <div style={style} dangerouslySetInnerHTML={{ __html: content }}></div>
        <>
          <div
            dangerouslySetInnerHTML={{ __html: attributes.text }}
            contentEditable={isEditing}
            // onMouseUp={(e) => setMouseUp(e)}
            // onKeyDown={(e) => setKeyDown(e)}
            // onBlur={(e) => setOnBlur(e)}
          ></div>
        </>
      )}

      {!isEditing && isBuilder && (
        // <div style={style} dangerouslySetInnerHTML={{ __html: content }}></div>
        <>
          <div
            style={{}}
            dangerouslySetInnerHTML={{ __html: attributes.text }}
            contentEditable={isEditing}
            // onMouseUp={(e) => setMouseUp(e)}
            // onKeyDown={(e) => setKeyDown(e)}
            // onBlur={(e) => setOnBlur(e)}
          ></div>
        </>
      )}

      {isBuilder &&
        isEditing &&
        editor &&
        !templateEditText &&
        builderEditor &&
        !builderEditor.template && (
          <>
            <div className="builder-component_text-data">
              <EditorContent
                id={domId + "_content"}
                content={attributes.text}
                editor={editor}
              ></EditorContent>
            </div>
          </>
        )}

      {isBuilder &&
        isEditing &&
        editor &&
        templateEditText &&
        builderEditor &&
        builderEditor.template && (
          <>
            <div className="builder-component_text-data">
              <EditorContent
                id={domId + "_content"}
                content={attributes.text}
                editor={editor}
              ></EditorContent>
            </div>
          </>
        )}
    </Button>
  );
}
